import { ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useT } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import type { ScenarioSummary } from "@/lib/scenarios";
import { ScenStatusDot } from "./atoms";

type MiniMapProps = {
  scenarios: readonly ScenarioSummary[];
};

// How many scenarios to surface in the mini chain. Five fits ~one
// screen vertically with the StartCard above it; longer chains
// become noise on Home — the journal + tree pages are for browsing.
const VISIBLE_COUNT = 5;

/**
 * Horizontal-ish sequential map of the next ~5 scenarios in the
 * learner's path. Visually anchored on the "current focus" item
 * (IN_PROGRESS / READY_FOR_CHECKPOINT first, then the next AVAILABLE,
 * then the very first scenario for a fresh user). Surrounding items
 * are dimmed; locked items are dimmed further but visible so the
 * user sees the shape of what's coming.
 *
 * Tap any non-locked row → goes to its detail; the full chain is one
 * click away via "Open full map → /skills/tree" below the mini map.
 */
export function MiniMap({ scenarios }: MiniMapProps) {
  const t = useT();

  // Pick the focus scenario. Priority:
  //   1. IN_PROGRESS / READY_FOR_CHECKPOINT
  //   2. First AVAILABLE (in stage order)
  //   3. First scenario period (for the truly empty case — should
  //      never happen with the new lock model but defensive)
  const sorted = [...scenarios].toSorted((a, b) => a.stage - b.stage || a.slug.localeCompare(b.slug));
  const focusIdx = (() => {
    const i1 = sorted.findIndex(
      (s) => s.progress?.status === "IN_PROGRESS" || s.progress?.status === "READY_FOR_CHECKPOINT",
    );
    if (i1 >= 0) return i1;
    const i2 = sorted.findIndex((s) => s.progress?.status === "AVAILABLE");
    if (i2 >= 0) return i2;
    return 0;
  })();

  // Window of VISIBLE_COUNT centered on the focus, clipped to the
  // sorted array bounds. Bias the window toward the start when the
  // focus is near the beginning so we don't show a half-empty window.
  const half = Math.floor(VISIBLE_COUNT / 2);
  let start = Math.max(0, focusIdx - half);
  const end = Math.min(sorted.length, start + VISIBLE_COUNT);
  // If the window ran off the right edge, slide it left so it still
  // shows VISIBLE_COUNT items where possible.
  if (end - start < VISIBLE_COUNT) {
    start = Math.max(0, end - VISIBLE_COUNT);
  }
  const window = sorted.slice(start, end);

  if (window.length === 0) return null;

  return (
    <section>
      <div className="mb-2 flex items-baseline justify-between px-0.5">
        <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-text-3">
          {t("home_skills.mini_map_title")}
        </span>
        <Link
          to="/skills/tree"
          className="inline-flex items-center gap-0.5 text-[12px] font-medium"
          style={{ color: "var(--scen-fg)" }}
        >
          {t("home_skills.mini_map_open_full")}
          <ChevronRight className="size-3" />
        </Link>
      </div>
      <div className="flex flex-col gap-1">
        {window.map((s, i) => {
          const status = s.progress?.status ?? "LOCKED";
          const isFocus = sorted.indexOf(s) === focusIdx;
          const isLocked = status === "LOCKED";
          const inner = (
            <div
              className={cn(
                "flex items-center gap-2.5 rounded-[var(--r-sm)] border bg-surface px-2.5 py-2 transition-colors",
                isFocus && "shadow-[var(--shadow-sm)]",
                !isFocus && "opacity-[0.78]",
                isLocked && "opacity-[0.55]",
              )}
              style={{
                borderColor: isFocus ? "var(--scen-solid)" : "var(--border)",
                borderWidth: isFocus ? 1.5 : 1,
              }}
            >
              <ScenStatusDot status={status} />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <span className="truncate text-[13px] font-medium text-text">{s.title}</span>
                  <span
                    className="shrink-0 rounded px-[5px] py-[1px] font-mono text-[9px] font-semibold text-text-3"
                    style={{ background: "var(--surface-2)" }}
                  >
                    {s.level}
                  </span>
                </div>
              </div>
            </div>
          );
          // LOCKED rows aren't navigable from the mini map — the
          // full tree is one tap away below; keep this surface
          // calm.
          if (isLocked) {
            return (
              <div key={s.slug}>
                {inner}
                {i < window.length - 1 && <ChainConnector />}
              </div>
            );
          }
          return (
            <div key={s.slug}>
              <Link to={`/skills/${s.slug}`} className="block">
                {inner}
              </Link>
              {i < window.length - 1 && <ChainConnector />}
            </div>
          );
        })}
      </div>
    </section>
  );
}

// Thin vertical line between rows so the chain reads as a path,
// not a list. 12px tall, centered on the status dot column.
function ChainConnector() {
  return (
    <div className="flex h-2 items-center pl-[18px]">
      <div className="h-full w-px" style={{ background: "var(--border)" }} />
    </div>
  );
}
